import { useEffect, useState } from 'react'
import type { ClinicalTrial } from '@alpha-edge/shared'
import { useApiList } from '../../hooks/use-api-list'
import { useDebounce } from '../../hooks/use-debounce'
import {
  PageHeader,
  DataTable,
  Pagination,
  Modal,
  FormField,
  ConfirmDialog,
  ColumnSettingsButton,
  btnPrimary,
  btnSecondary,
  btnDanger,
  input,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { useModal } from '../../hooks/use-modal'
import { api, ApiError } from '../../services/api'
import { useTableColumns } from '../../hooks/use-table-columns'

interface Form {
  protocolId: string
  sponsor: string
  name: string
  description: string
  barcodeFormat: string
  status: string
  startDate: string
  endDate: string
  cohortsJson: string
}

const defaults: Form = {
  protocolId: '',
  sponsor: '',
  name: '',
  description: '',
  barcodeFormat: '',
  status: 'planning',
  startDate: '',
  endDate: '',
  cohortsJson: '[]',
}

export function ClinicalTrialListPage() {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)
  const { data, total, page, loading, setPage, refresh } = useApiList<ClinicalTrial>({
    path: '/clinical-trials',
    filters: { search: debouncedSearch || undefined },
  })
  const { tableId: _, ...tableColumnProps } = useTableColumns('clinical-trials')
  const [modalOpen, setModalOpen] = useState(false)
  const [selected, setSelected] = useState<ClinicalTrial | null>(null)
  const [isNew, setIsNew] = useState(false)
  const [form, setForm] = useState<Form>(defaults)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const archiveModal = useModal()
  const [archiving, setArchiving] = useState(false)

  useEffect(() => {
    if (!modalOpen) return
    setError(null)
    if (isNew || !selected) {
      setForm(defaults)
      return
    }
    setForm({
      protocolId: selected.protocolId,
      sponsor: selected.sponsor ?? '',
      name: selected.name,
      description: selected.description ?? '',
      barcodeFormat: selected.barcodeFormat ?? '',
      status: selected.status,
      startDate: selected.startDate ? selected.startDate.slice(0, 10) : '',
      endDate: selected.endDate ? selected.endDate.slice(0, 10) : '',
      cohortsJson: JSON.stringify(selected.cohorts, null, 2),
    })
  }, [modalOpen, isNew, selected])

  const update = <K extends keyof Form>(k: K, v: Form[K]) => setForm((prev) => ({ ...prev, [k]: v }))

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      let cohorts: unknown
      try {
        cohorts = JSON.parse(form.cohortsJson || '[]')
        if (!Array.isArray(cohorts)) throw new Error('cohorts must be an array')
      } catch (err) {
        setError(`JSON invalid: ${err instanceof Error ? err.message : String(err)}`)
        setSaving(false)
        return
      }
      const payload: Record<string, unknown> = {
        name: form.name,
        status: form.status,
        cohorts,
      }
      if (isNew) payload.protocolId = form.protocolId
      if (form.sponsor) payload.sponsor = form.sponsor
      if (form.description) payload.description = form.description
      if (form.barcodeFormat) payload.barcodeFormat = form.barcodeFormat
      if (form.startDate) payload.startDate = `${form.startDate}T00:00:00.000Z`
      if (form.endDate) payload.endDate = `${form.endDate}T23:59:59.000Z`
      if (isNew) await api.post<ClinicalTrial>('/clinical-trials', payload)
      else await api.put<ClinicalTrial>(`/clinical-trials/${selected?.id}`, payload)
      refresh()
      setModalOpen(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleArchive() {
    if (!selected) return
    setArchiving(true)
    try {
      await api.delete(`/clinical-trials/${selected.id}`)
      refresh()
      archiveModal.close()
      setModalOpen(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to archive')
    } finally {
      setArchiving(false)
    }
  }

  const columns: readonly Column<ClinicalTrial>[] = [
    {
      key: 'protocol',
      header: 'Protocol',
      sortable: true,
      filterable: true,
      render: (t) => <strong>{t.protocolId}</strong>,
    },
    { key: 'name', header: 'Name', sortable: true, filterable: true, render: (t) => t.name },
    { key: 'sponsor', header: 'Sponsor', render: (t) => t.sponsor || '--' },
    { key: 'cohorts', header: 'Cohorts', render: (t) => `${t.cohorts.length}` },
    { key: 'status', header: 'Status', sortable: true, render: (t) => t.status },
  ]

  const title = isNew ? 'New Clinical Trial' : `${form.protocolId} - ${form.name}`

  return (
    <div>
      <PageHeader
        title="Clinical Trials"
        subtitle={`${total} total`}
        actions={
          <>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button
              type="button"
              onClick={() => {
                setSelected(null)
                setIsNew(true)
                setModalOpen(true)
              }}
              style={btnPrimary}
            >
              Add Trial
            </button>
          </>
        }
      />
      <p style={{ color: colors.textMuted, fontSize: '0.875rem', margin: '0 0 1rem' }}>
        Trial-scoped barcode formats can override the per-lab-client default for orders tagged to a cohort (engine
        wire-up follows once the cohort-tagging flow lands).
      </p>
      <div style={{ marginBottom: '1rem' }}>
        <input
          style={{ ...input, width: '320px' }}
          placeholder="Search by protocol ID or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={data}
            keyField="id"
            onRowClick={(t) => {
              setSelected(t)
              setIsNew(false)
              setModalOpen(true)
            }}
            emptyMessage="No clinical trials yet."
            {...tableColumnProps}
          />
          <Pagination page={page} total={total} limit={25} onPageChange={setPage} />
        </>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={title} width="780px">
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.75rem' }}>
            <FormField
              name="protocolId"
              label="Protocol ID"
              required
              value={form.protocolId}
              onChange={(v) => update('protocolId', v)}
              disabled={!isNew}
            />
            <FormField name="name" label="Name" required value={form.name} onChange={(v) => update('name', v)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem', marginTop: '0.75rem' }}>
            <FormField name="sponsor" label="Sponsor" value={form.sponsor} onChange={(v) => update('sponsor', v)} />
            <FormField name="status" label="Status" required>
              <select style={input} value={form.status} onChange={(e) => update('status', e.target.value)}>
                <option value="planning">Planning</option>
                <option value="enrolling">Enrolling</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="closed">Closed</option>
              </select>
            </FormField>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginTop: '0.75rem' }}>
            <FormField
              name="startDate"
              label="Start Date"
              type="date"
              value={form.startDate}
              onChange={(v) => update('startDate', v)}
            />
            <FormField
              name="endDate"
              label="End Date"
              type="date"
              value={form.endDate}
              onChange={(v) => update('endDate', v)}
            />
            <FormField
              name="barcodeFormat"
              label="Barcode Format"
              value={form.barcodeFormat}
              onChange={(v) => update('barcodeFormat', v)}
            />
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <FormField name="description" label="Description">
              <textarea
                style={{ ...input, minHeight: '60px', resize: 'vertical' }}
                value={form.description}
                onChange={(e) => update('description', e.target.value)}
              />
            </FormField>
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <FormField name="cohortsJson" label="Cohorts (JSON array)">
              <textarea
                style={{
                  ...input,
                  minHeight: '120px',
                  resize: 'vertical',
                  fontFamily: 'monospace',
                  fontSize: '0.8125rem',
                }}
                value={form.cohortsJson}
                onChange={(e) => update('cohortsJson', e.target.value)}
                placeholder='[{"code":"A","name":"Treatment","targetEnrollment":50}]'
              />
            </FormField>
          </div>
          {error && (
            <div
              style={{
                marginTop: '0.75rem',
                color: colors.danger,
                padding: '0.5rem 0.75rem',
                background: colors.bgError,
                border: `1px solid ${colors.borderError}`,
                borderRadius: '3px',
                fontSize: '0.875rem',
              }}
            >
              {error}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
            {!isNew && (
              <button
                type="button"
                onClick={archiveModal.open}
                style={{ ...btnDanger, marginRight: 'auto' }}
                disabled={saving || archiving}
              >
                Archive
              </button>
            )}
            <button type="button" onClick={() => setModalOpen(false)} style={btnSecondary} disabled={saving}>
              Cancel
            </button>
            <button type="button" onClick={handleSave} style={btnPrimary} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={archiveModal.isOpen}
        onClose={archiveModal.close}
        onConfirm={handleArchive}
        title="Archive Clinical Trial"
        message={`Archive ${form.name}? Existing enrollments are preserved.`}
        confirmLabel="Archive"
        loading={archiving}
      />
    </div>
  )
}
